import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  TextField,
  Grid,
  Stack,
  Divider,
  IconButton,
  alpha,
  MenuItem,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Badge as LicenseIcon
} from '@mui/icons-material';
import { LoginHero } from '../../components/design-system';
import { authAPI } from '../../services/api';

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const Signup = ({ onSignup }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Get product from URL parameter (e.g., ?product=lipoderma)
  const urlProduct = searchParams.get('product');
const initialProduct = urlProduct === 'lipoderma' ? 'Lipoderma' : 
                       urlProduct === 'equicenta' ? 'Equicenta' : '';
  
  const [selectedProduct, setSelectedProduct] = useState(initialProduct);

  const [signupForm, setSignupForm] = useState({
    // Personal Information
    prefix: '',
    firstName: '',
    lastName: '',
    suffix: '',
    email: '',
    phone: '',

    // Professional Information
    clinicName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    medicalLicenseNumber: '',

    // User Type - always client for public signup
    userType: 'CLIENT'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSignupForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Product required
    if (!selectedProduct) {
      setError('Please select a product (required).');
      setLoading(false);
      return;
    }

    // Map product -> organization
  const organization = selectedProduct === 'Lipoderma' ? 'BARE' : 'EPL';

    const userData = {
      prefix: signupForm.prefix,
      firstName: signupForm.firstName,
      lastName: signupForm.lastName,
      suffix: signupForm.suffix,
      email: signupForm.email,
      phone: signupForm.phone,
      clinicName: signupForm.clinicName,
      address: signupForm.address,
      city: signupForm.city,
      state: signupForm.state,
      zipCode: signupForm.zipCode,
      medicalLicenseNumber: signupForm.medicalLicenseNumber,
      userType: 'CLIENT',
      organization,
      product: selectedProduct,
    };

    try {
      const response = await authAPI.register(userData);
      
      if (response.success) {
        setLoading(false);
        const productSlug = selectedProduct.toLowerCase();
        navigate(`/signup/pending?product=${encodeURIComponent(productSlug)}`);
      } else {
        throw new Error(response.message || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <LoginHero>
      {/* Home Button */}
      <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
        <IconButton
          component={Link}
          to="/"
          sx={{
            color: 'white',
            bgcolor: alpha('#ffffff', 0.1),
            backdropFilter: 'blur(10px)',
            '&:hover': {
              bgcolor: alpha('#ffffff', 0.2),
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <HomeIcon />
        </IconButton>
      </Box>
      
      <Container maxWidth="md">
        <Card sx={{ 
          p: 4,
          backdropFilter: 'blur(20px)',
          bgcolor: alpha('#ffffff', 0.95),
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: `1px solid ${alpha('#ffffff', 0.2)}`
        }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#0f172a',
                  mb: 1 
                }}
              >
                Healthcare Provider Signup
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: '#475569' }}
              >
                Create your healthcare provider account to access the platform
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSignup}>
              <Grid container spacing={3}>
                {/* Personal Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    Personal Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Prefix"
                    name="prefix"
                    value={signupForm.prefix}
                    onChange={handleInputChange}
                    placeholder="Dr., Mr., Ms., etc."
                    helperText="e.g., Dr."
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={signupForm.firstName}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={signupForm.lastName}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="Suffix"
                    name="suffix"
                    value={signupForm.suffix}
                    onChange={handleInputChange}
                    placeholder="DVM, PhD, etc."
                    helperText="e.g., DVM, PhD"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    name="email"
                    value={signupForm.email}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={signupForm.phone}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                  />
                </Grid>

                {/* Professional Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 2, mt: 2 }}>
                    Professional Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Clinic/Practice Name"
                    name="clinicName"
                    value={signupForm.clinicName}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <BusinessIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={signupForm.address}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={signupForm.city}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="State"
                    name="state"
                    value={signupForm.state}
                    onChange={handleInputChange}
                    required
                  >
                    {US_STATES.map((state) => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="zipCode"
                    value={signupForm.zipCode}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Medical License Number"
                    name="medicalLicenseNumber"
                    value={signupForm.medicalLicenseNumber}
                    onChange={handleInputChange}
                    required
                    InputProps={{
                      startAdornment: <LicenseIcon sx={{ color: '#64748b', mr: 1 }} />
                    }}
                    helperText="Enter your state medical license number"
                  />
                </Grid>

                {/* Product selection (required) */}
                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend" sx={{ mb: 1, color: '#1e293b' }}>
                      Select product (required)
                    </FormLabel>
                    <RadioGroup
                      row
                      name="product"
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                    >
                      <FormControlLabel value="Lipoderma" control={<Radio />} label="Bare Biologics: Lipoderma" />
                      <FormControlLabel value="Equicenta" control={<Radio />} label="Equine Performance Labs: equicenta CTM" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      bgcolor: '#0d9488',
                      '&:hover': { bgcolor: '#0f766e' },
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      mt: 2
                    }}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </Grid>

                {/* Navigation Links */}
                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    Already have an account?
                  </Typography>
                  <Button
                    component={Link}
                    to="/login"
                    variant="outlined"
                    sx={{ 
                      color: '#0d9488',
                      borderColor: '#0d9488',
                      '&:hover': { 
                        bgcolor: '#f0fdfa',
                        borderColor: '#0f766e'
                      },
                      mr: 2
                    }}
                  >
                    Sign In Instead
                  </Button>
                  <Button
                    component={Link}
                    to="/"
                    startIcon={<ArrowBackIcon />}
                    sx={{ 
                      color: '#475569',
                      '&:hover': { color: '#0d9488' }
                    }}
                  >
                    Back to Home
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Container>
    </LoginHero>
  );
};

export default Signup;
